import { useAuthState } from '@/hooks/useAuth'
import { AuthModal } from '@/components/auth/AuthModal'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { useState, useEffect } from 'react'
import { auth } from '@/lib/firebase'

interface AdminAuthGuardProps {
  children: React.ReactNode
}

const ADMIN_EMAILS = [
  'jari@getmoretats.com',
  'keijo@getmoretats.com',
  'lotta@getmoretats.com'
] as const

export function isAdminUserEmail(email: string | null | undefined): boolean {
  return !!email && (ADMIN_EMAILS as readonly string[]).includes(email)
}

export function AdminAuthGuard({ children }: AdminAuthGuardProps) {
  const { user, loading } = useAuthState()
  const [tokenRefreshed, setTokenRefreshed] = useState(false)
  const [refreshingToken, setRefreshingToken] = useState(false)
  const [tokenError, setTokenError] = useState<string | null>(null)

  // Force token refresh when user is authenticated to ensure email claim is present
  useEffect(() => {
    const refreshTokenIfNeeded = async () => {
      if (user && !tokenRefreshed && !refreshingToken) {
        setRefreshingToken(true)
        try {
          // Force refresh the ID token to get latest claims including email
          const token = await user.getIdToken(true)
          setTokenRefreshed(true)
          setTokenError(null)
          
          // Debug: Log token information
          console.log('✅ Admin token refreshed successfully')
          console.log('🔍 User email:', user.email)
          console.log('🔍 User email verified:', user.emailVerified)
          console.log('🔍 Is admin email:', isAdminUserEmail(user.email))
          
          // Decode token to check claims (for debugging)
          try {
            const tokenPayload = JSON.parse(atob(token.split('.')[1]))
            console.log('🔍 Token email claim:', tokenPayload.email)
            console.log('🔍 Token email_verified claim:', tokenPayload.email_verified)
          } catch (decodeError) {
            console.log('⚠️ Could not decode token for debugging')
          }
        } catch (error) {
          console.error('❌ Error refreshing admin token:', error)
          setTokenError('Failed to refresh authentication token. Please try signing out and back in.')
        } finally {
          setRefreshingToken(false)
        }
      }
    }

    refreshTokenIfNeeded()
  }, [user, tokenRefreshed, refreshingToken])

  if (loading || (user && refreshingToken)) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-foreground mx-auto mb-4"></div>
          <p className="text-foreground">
            {loading ? 'Loading...' : 'Refreshing authentication...'}
          </p>
        </div>
      </div>
    )
  }

  // Show token refresh error
  if (tokenError) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-md border-destructive">
          <CardHeader className="text-center">
            <CardTitle className="text-destructive">Authentication Error</CardTitle>
            <CardDescription>
              There was a problem with your authentication
            </CardDescription>
          </CardHeader>
          <CardContent className="text-center space-y-4">
            <p className="text-sm text-muted-foreground">
              {tokenError}
            </p>
            <Button 
              onClick={async () => {
                try {
                  console.log('[AdminAuthGuard] Starting sign-out...')
                  
                  // Clean up iOS-specific resources first
                  const { cleanupIosOnSignOut } = await import('@/lib/notifications')
                  const { Capacitor } = await import('@capacitor/core')
                  const isIOSNative = Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios'
                  
                  if (isIOSNative) {
                    console.log('[AdminAuthGuard] Cleaning up iOS resources...')
                    await cleanupIosOnSignOut()
                  }
                  
                  // Sign out from Firebase
                  console.log('[AdminAuthGuard] Signing out from Firebase...')
                  await auth.signOut()
                  // This will trigger a page reload and redirect to login
                } catch (error: any) {
                  console.error('[AdminAuthGuard] Error signing out:', error)
                  console.error('[AdminAuthGuard] Error details:', {
                    code: error?.code,
                    message: error?.message,
                    name: error?.name
                  })
                }
              }}
              variant="outline"
            >
              Sign Out & Try Again
            </Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  // User not authenticated
  if (!user) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <CardTitle>Admin Access Required</CardTitle>
            <CardDescription>
              Please sign in with your admin account to access this page
            </CardDescription>
          </CardHeader>
          <CardContent>
            <AuthModal
              isOpen={true}
              onOpenChange={() => {}}
              dismissible={false}
              defaultMode="login"
              source="admin_access"
            />
          </CardContent>
        </Card>
      </div>
    )
  }

  // User authenticated but not admin
  if (!isAdminUserEmail(user.email)) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <CardTitle>Access Denied</CardTitle>
            <CardDescription>
              You don't have permission to access this admin area.
            </CardDescription>
          </CardHeader>
          <CardContent className="text-center space-y-4">
            <p className="text-sm text-muted-foreground mb-4">
              Signed in as: {user.email}
            </p>
            <p className="text-sm text-muted-foreground mb-4">
              Contact an administrator if you believe this is an error.
            </p>
            <Button 
              onClick={async () => {
                try {
                  console.log('[AdminAuthGuard] Starting sign-out...')
                  
                  // Clean up iOS-specific resources first
                  const { cleanupIosOnSignOut } = await import('@/lib/notifications')
                  const { Capacitor } = await import('@capacitor/core')
                  const isIOSNative = Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios'
                  
                  if (isIOSNative) {
                    console.log('[AdminAuthGuard] Cleaning up iOS resources...')
                    await cleanupIosOnSignOut()
                  }
                  
                  // Sign out from Firebase
                  console.log('[AdminAuthGuard] Signing out from Firebase...')
                  await auth.signOut()
                } catch (error: any) {
                  console.error('[AdminAuthGuard] Error signing out:', error)
                  console.error('[AdminAuthGuard] Error details:', {
                    code: error?.code,
                    message: error?.message,
                    name: error?.name
                  })
                }
              }}
              variant="outline"
              size="sm"
            >
              Sign Out
            </Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  // User is authenticated and is admin - but ensure token has been refreshed
  if (!tokenRefreshed) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-foreground mx-auto mb-4"></div>
          <p className="text-foreground">Verifying admin permissions...</p>
        </div>
      </div>
    )
  }

  return <>{children}</>
}
